import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, toast } from '../ui';
import { useConsent } from './consent';
import styles from './consent.module.css';

/**
 * First-visit privacy choice, also reopened from "Cookie settings". Non-blocking (the page
 * stays usable), equal-weight buttons, nothing optional is on until the user says so.
 */
export function ConsentBanner() {
  const { t } = useTranslation();
  const { decided, preferences, settingsOpen, choose, closeSettings } = useConsent();
  const [details, setDetails] = useState(false);
  const [wantPreferences, setWantPreferences] = useState(preferences);
  const rootRef = useRef<HTMLElement>(null);
  const visible = !decided || settingsOpen;

  useEffect(() => {
    if (!settingsOpen) return;
    setDetails(true);
    setWantPreferences(useConsent.getState().preferences);
    rootRef.current?.focus();
  }, [settingsOpen]);

  if (!visible) return null;

  const decide = (prefs: boolean) => {
    choose(prefs);
    setDetails(false);
    if (settingsOpen) toast.success(t('legal.consent.saved'));
  };

  return (
    <section
      ref={rootRef}
      tabIndex={-1}
      className={styles.banner}
      aria-labelledby="consent-title"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && settingsOpen) closeSettings();
      }}
    >
      <h2 id="consent-title" className={styles.title}>
        {t('legal.consent.title')}
      </h2>
      <p className={styles.body}>
        {t('legal.consent.body')} <a href="/privacy">{t('legal.consent.learnMore')}</a>
      </p>

      {details && (
        <fieldset className={styles.options}>
          <legend className="visually-hidden">{t('legal.consent.title')}</legend>
          <label className={styles.option}>
            <input type="checkbox" checked disabled />
            <span>
              <strong>{t('legal.consent.essential.title')}</strong>
              <span className={styles.hint}>{t('legal.consent.essential.body')}</span>
            </span>
          </label>
          <label className={styles.option}>
            <input
              type="checkbox"
              checked={wantPreferences}
              onChange={(e) => setWantPreferences(e.target.checked)}
            />
            <span>
              <strong>{t('legal.consent.preferences.title')}</strong>
              <span className={styles.hint}>{t('legal.consent.preferences.body')}</span>
            </span>
          </label>
        </fieldset>
      )}

      <div className={styles.actions}>
        <Button onClick={() => decide(false)}>{t('legal.consent.essentialOnly')}</Button>
        {details ? (
          <Button onClick={() => decide(wantPreferences)}>{t('legal.consent.save')}</Button>
        ) : (
          <Button onClick={() => setDetails(true)}>{t('legal.consent.customize')}</Button>
        )}
        <Button variant="primary" onClick={() => decide(true)}>
          {t('legal.consent.acceptAll')}
        </Button>
      </div>
    </section>
  );
}
